import { listMedicalUnits } from "@/core/admin/establishment-store";
import { DEMO_HOSPITAL_STAFF } from "./demo-data";
import { rhRequest, RhApiError } from "./rh-client";
import type {
  HospitalDepartmentSummary,
  HospitalScheduleRow,
  HospitalStaffMember,
  StaffLoadSource,
} from "./types";

interface RhEmployeeDto {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  position: string;
  status: string;
}

const SHIFT_ROTATION: Array<{ label: string; code: HospitalScheduleRow["shiftCode"] }> = [
  { label: "Matin (6h–14h)", code: "morning" },
  { label: "Après-midi (14h–22h)", code: "afternoon" },
  { label: "Nuit (22h–6h)", code: "night" },
  { label: "Garde", code: "oncall" },
];

function mapEmployee(dto: RhEmployeeDto): HospitalStaffMember {
  return {
    id: dto.id,
    firstName: dto.firstName,
    lastName: dto.lastName,
    email: dto.email,
    department: dto.department,
    position: dto.position,
    status: dto.status,
  };
}

export async function loadHospitalStaff(): Promise<{
  staff: HospitalStaffMember[];
  source: StaffLoadSource;
}> {
  try {
    const rows = await rhRequest<RhEmployeeDto[]>("/employees");
    return { staff: rows.map(mapEmployee), source: "api" };
  } catch {
    return { staff: DEMO_HOSPITAL_STAFF, source: "demo" };
  }
}

export function staffFullName(member: HospitalStaffMember): string {
  return `${member.firstName} ${member.lastName}`.trim();
}

export function buildDepartmentSummaries(
  staff: HospitalStaffMember[],
): HospitalDepartmentSummary[] {
  const counts = new Map<string, number>();
  for (const member of staff) {
    if (member.status !== "ACTIVE") continue;
    const key = member.department.trim() || "Non assigné";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const fromRh: HospitalDepartmentSummary[] = [...counts.entries()]
    .map(([name, headcount]) => ({
      id: `dept-${name.toLowerCase().replace(/\s+/g, "-")}`,
      name,
      code: name.slice(0, 4).toUpperCase(),
      headcount,
      source: "rh" as const,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "fr"));

  const unitNames = new Set(
    listMedicalUnits()
      .filter((u) => u.isActive)
      .map((u) => u.name),
  );
  for (const unitName of unitNames) {
    if (!fromRh.some((d) => d.name === unitName)) {
      fromRh.push({
        id: `unit-${unitName.toLowerCase().replace(/\s+/g, "-")}`,
        name: unitName,
        code: unitName.slice(0, 4).toUpperCase(),
        headcount: 0,
        source: "establishment",
      });
    }
  }

  return fromRh.sort((a, b) => a.name.localeCompare(b.name, "fr"));
}

export function buildScheduleRows(staff: HospitalStaffMember[]): HospitalScheduleRow[] {
  return staff
    .filter((m) => m.status === "ACTIVE")
    .map((member, index) => {
      const shift = SHIFT_ROTATION[index % SHIFT_ROTATION.length];
      return {
        staffId: member.id,
        staffName: staffFullName(member),
        department: member.department,
        position: member.position,
        shiftLabel: shift.label,
        shiftCode: shift.code,
      };
    })
    .sort((a, b) => a.department.localeCompare(b.department, "fr"));
}

export async function loadHospitalSchedules(): Promise<{
  rows: HospitalScheduleRow[];
  source: StaffLoadSource;
}> {
  try {
    const rows = await rhRequest<HospitalScheduleRow[]>("/schedules");
    return { rows, source: "api" };
  } catch {
    const { staff, source } = await loadHospitalStaff();
    return { rows: buildScheduleRows(staff), source };
  }
}

export function isRhApiOfflineError(err: unknown): boolean {
  return err instanceof RhApiError && err.status === 0;
}
